/** @format */

import * as R from 'ramda';

import {OauthProvider, YalperAdType, YalperPlatform} from 'graphql/generated/types';

import {YalperAdTypeWithStats} from '../types';

export interface TokenWithProviderAndError {
  provider: OauthProvider;
  lastError?: string | null;
}

interface TokenWithValidation extends TokenWithProviderAndError {
  isValid: boolean;
}

const MAPPING: Record<OauthProvider, YalperAdType> = {
  YANDEX: 'CONTEXT',
  GOOGLE: 'CONTEXT',
  FACEBOOK: 'TARGET',
  VKONTAKTE: 'TARGET',
  VKONTAKTE_ADS: 'TARGET',
  METRIKA: 'CONTEXT',
  WEBDAV: 'CONTEXT',
};

export const ALL_PLATFORMS: OauthProvider[] = [
  'GOOGLE',
  'FACEBOOK',
  'YANDEX',
  'VKONTAKTE',
  'VKONTAKTE_ADS',
  'METRIKA',
  'WEBDAV',
];

const RuPlatforms = ['YANDEX', 'VKONTAKTE', 'VKONTAKTE_ADS'];

export const platformsFromAdType = (adType: YalperAdTypeWithStats): OauthProvider[] => {
  const entries = Object.entries(MAPPING) as [OauthProvider, YalperAdType][];
  return entries.filter(v => v[1] === adType).map(v => v[0]);
};

export const initialAdType = <T extends TokenWithProviderAndError>(
  tokens: ReadonlyArray<T>,
): YalperAdType | null => {
  const keys = Object.keys(MAPPING) as OauthProvider[];
  const provider = keys.find(v => userProviders(tokens).includes(v));

  if (!provider) {
    return null;
  }

  // Return null (not undefined) - this value will be saved
  // to local storage and parsed in CompanyProvider
  return MAPPING[provider];
};

export const isAdTypeConnected = <T extends TokenWithProviderAndError>(
  tokens: T[],
  adType: YalperAdType,
): boolean => {
  return userProviders(tokens).some(v => platformsFromAdType(adType).includes(v));
};

export const filterPlatformsByTokens = <T extends TokenWithProviderAndError>(
  tokens: ReadonlyArray<T>,
  platforms: YalperPlatform[],
): YalperPlatform[] => platforms.filter(v => tokens.find(token => token.provider === v));

const userProviders = <T extends TokenWithProviderAndError>(
  tokens: ReadonlyArray<T>,
): OauthProvider[] => {
  return R.pipe(
    R.filter<T>(v => v && !v.lastError),
    R.map(v => v.provider),
  )(tokens);
};

export const rejectRuPlatforms = (platforms: YalperPlatform[]): YalperPlatform[] => {
  return platforms.filter(v => !RuPlatforms.includes(v));
};

export const isCertainProvider = <T extends TokenWithValidation>(
  tokens: ReadonlyArray<T>,
  providerToCompare: OauthProvider,
): boolean => {
  return tokens.some(
    token => token.isValid && !token.lastError && token.provider === providerToCompare,
  );
};
